import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { CookieService } from 'ngx-cookie';
import { of, Observable, BehaviorSubject } from 'rxjs';
import { userSessionTokenCookieName } from 'src/app/shared/cookie-key';
import { NotificationService } from 'src/app/shared/notification/notification.service';
import { LoginState } from './login-state.interface';
import { catchError, tap } from 'rxjs/operators';
import { ConfigService } from 'src/app/services/config/config.service';

@Injectable({ providedIn: 'root' })
export class LoginService {
    constructor(
        private apollo: Apollo,
        private cookieService: CookieService,
        private notificationService: NotificationService,
        private readonly configService: ConfigService
    ) {}

    private loginState = new BehaviorSubject<LoginState>(null);
    private updateState(state: LoginState) {
        this.loginState.next(state);
    }

    public get loginState$(): Observable<LoginState> {
        return this.loginState;
    }

    public logInUserFromCookie(): boolean {
        const userSessionToken: string = this.cookieService.get(
            userSessionTokenCookieName
        );
        if (userSessionToken?.length) {
            this.initiateLogIn();
            this.getLoggedInUserDataAndSetState(userSessionToken, true);
            return true;
        }
        return false;
    }

    private initiateLogIn() {
        const loginState: LoginState = {
            firstName: '',
            lastName: '',
            clientID: 0,
            userAccountGuid: '',
            isLoggingIn: true,
        };
        this.updateState(loginState);
    }

    public logIn(email: string, password: string) {
        this.initiateLogIn();
        const loginMutation = gql`
            mutation Mutation(
                $customerID: ID!
                $email: String!
                $password: String!
            ) {
                loginUser(
                    customerID: $customerID
                    email: $email
                    password: $password
                ) {
                    userClientAuthTokens {
                        accessToken
                        idToken
                        refreshToken
                    }
                    userSession {
                        expirationDate
                        token
                    }
                }
            }
        `;

        this.apollo
            .mutate({
                mutation: loginMutation,
                variables: {
                    email: email,
                    password: password,
                },
            })
            .subscribe(
                (response: any) => {
                    this.pushTokensToClientAuth(
                        response.data?.loginUser?.userClientAuthTokens
                    );
                    if (response?.data?.loginUser?.userSession.token) {
                        this.getLoggedInUserDataAndSetState(
                            response.data.loginUser.userSession.token,
                            false
                        );

                        this.cookieService.put(
                            userSessionTokenCookieName,
                            response.data.loginUser.userSession.token,
                            {
                                expires:
                                    response.data.loginUser.userSession
                                        .expirationDate,
                                storeUnencoded: true,
                            }
                        );
                    } else {
                        this.handleLogInFailure();
                    }
                },
                (_err) => {
                    return this.handleLogInFailure();
                }
            );
    }

    public logInUserFromSession(id_token: string): Observable<any> {
        this.initiateLogIn();
        const createSessionMutation = gql`
            mutation Mutation($customerID: ID!, $id_token: String!) {
                createSession(customerID: $customerID, id_token: $id_token) {
                    userSession {
                        expirationDate
                        token
                    }
                }
            }
        `;

        return this.apollo
            .mutate({
                mutation: createSessionMutation,
                variables: {
                    id_token: id_token,
                },
            })
            .pipe(
                tap((response: any) => {
                    if (response?.data?.createSession?.userSession?.token) {
                        const {
                            token,
                            expirationDate,
                        } = response.data.createSession.userSession;
                        this.getLoggedInUserDataAndSetState(token, true);

                        this.cookieService.put(
                            userSessionTokenCookieName,
                            token,
                            {
                                expires: expirationDate,
                                storeUnencoded: true,
                            }
                        );
                    } else {
                        this.handleLogInFailure(true);
                    }
                }),
                catchError(() => {
                    return this.handleLogInFailure(true);
                })
            );
    }

    private getLoggedInUserDataAndSetState(
        userSessionToken: string,
        shouldHandleFailureSilently: boolean
    ) {
        const getUserDataQuery = gql`
            query Query($customerID: ID!, $token: ID!) {
                userAccountInfo(customerID: $customerID, token: $token) {
                    userAccountGuid
                    firstName
                    lastName
                    clientID
                }
            }
        `;

        this.apollo
            .query({
                query: getUserDataQuery,
                variables: {
                    token: userSessionToken,
                },
            })
            .subscribe(
                (getDataResponse: any) => {
                    if (getDataResponse?.data?.userAccountInfo) {
                        const loginState: LoginState = {
                            firstName:
                                getDataResponse.data.userAccountInfo.firstName,
                            lastName:
                                getDataResponse.data.userAccountInfo.lastName,
                            clientID:
                                getDataResponse.data.userAccountInfo.clientID,
                            userAccountGuid:
                                getDataResponse.data.userAccountInfo
                                    .userAccountGuid,
                            isLoggingIn: false,
                        };
                        this.updateState(loginState);
                    } else {
                        this.handleLogInFailure(shouldHandleFailureSilently);
                    }
                },
                (_err) => {
                    return this.handleLogInFailure(shouldHandleFailureSilently);
                }
            );
    }

    private handleLogInFailure(
        shouldHandleSilently: boolean = false
    ): Observable<any> {
        this.logOut();

        if (!shouldHandleSilently) {
            this.notificationService.showFailure(
                'Login failed. Please try again.'
            );
        }

        return of(null);
    }

    private pushTokensToClientAuth(authTokens: any) {
        if (
            authTokens &&
            authTokens.idToken &&
            authTokens.accessToken &&
            authTokens.refreshToken
        ) {
            const tokens = {
                idToken: authTokens.idToken,
                accessToken: authTokens.accessToken,
                refreshToken: authTokens.refreshToken,
            };
            this.saveTokensToClientAuthFrontendCookies(tokens);
        }
    }

    private saveTokensToClientAuthFrontendCookies(tokens: any) {
        const origin = this.configService.config.clientAuthUrl;
        const url = `${origin}/UpdateTokens`; // Client Auth URL which routes to UpdateTokens
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = url;
        document.body.appendChild(iframe);

        const handleMessage = (event: MessageEvent) => {
            if (
                event?.origin === origin &&
                event?.data?.result === 'success' &&
                event?.data?.data === 'Ready'
            ) {
                // Once the "Ready" message is received, send tokens to the iframe to save to cookies
                iframe.contentWindow?.postMessage(tokens, url);
                // Remove the event listener after the message is posted
                window.removeEventListener('message', handleMessage);
            }
        };

        // Add an event listener to listen "success" and "Ready" message from iframe
        window.addEventListener('message', handleMessage);
    }

    logOut() {
        this.updateState(null);
        this.cookieService.remove(userSessionTokenCookieName);
    }
}
